import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { Paginate } from "@model/Paginate";
import { OrientationDeadline } from "@model/OrientationDeadline";
import { environment } from "./../environments/environment";
import { Institution } from "@model/admin/institution";
import { Partnership } from "@model/admin/partnership";

@Injectable({ providedIn: "root" })
export class InstitutionService {
  constructor(private http: HttpClient) {}

  getStats(
    institutionName: string,
    per_page: number = 10,
    page: number = 1,
    advisorId: number = null
  ): Observable<any> {
    return this.http.get(
      `${
        environment.base_url
      }/institutions/${institutionName}/get_stats?page=${page}&per_page=${per_page}${
        advisorId ? "&advisor_id=" + advisorId : ""
      }`
    );
  }

  paginateInstitutionResearcher(
    institutionName: string,
    per_page: number = 20,
    page: number = 1,
    term: string = "",
    month: number = new Date().getMonth() + 1,
    year: number = new Date().getFullYear(),
    prod_or_register: string
  ): Observable<any> {
    return this.http.get(
      `${environment.base_url}/institutions/${institutionName}/get_researchers?per_page=${per_page}&page=${page}&term=${term}&year=${year.toString()}&month=${month}&prod_or_register=${prod_or_register}`
    );
  }

  paginateInstitutionStudents(
    institutionName: string,
    per_page: number = 20,
    page: number = 1,
    term: string = ""
  ): Observable<any> {
    return this.http.get(
      `${environment.base_url}/institutions/${institutionName}/get_students?per_page=${per_page}&page=${page}&term=${term}`
    );
  }

  paginateInstitutionTeachers(
    institutionName: string,
    per_page: number = 20,
    page: number = 1,
    term: string = ""
  ): Observable<any> {
    return this.http.get(
      `${environment.base_url}/institutions/${institutionName}/get_teachers?per_page=${per_page}&page=${page}&term=${term}`
    );
  }

  countResearchers(institutionName: string) {
    return this.http.get(
      `${environment.base_url}/institutions/${institutionName}/count_researchers`
    );
  }

  countTeachers(institutionName: string) {
    return this.http.get(
      `${environment.base_url}/institutions/${institutionName}/count_teachers`
    );
  }

  countStudents(institutionName: string) {
    return this.http.get(
      `${environment.base_url}/institutions/${institutionName}/count_students`
    );
  }

  savePrice(partnership: Partnership, institutionId: number): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/institutions/save_price`,
      {
        partnership,
        institution_id: institutionId,
      }
    );
  }

  get(institutionId: number): Observable<Institution> {
    return this.http.get<Institution>(
      `${environment.base_url}/institutions/${institutionId}`
    );
  }

  getInstitutionByName(institutionName: string) {
    return this.http.get<Institution>(
      `${environment.base_url}/institutions/${institutionName}`
    )
  }

  getInstitutions(): Observable<any> {
    return this.http.get(
      `${environment.base_url}/institutions/get_institutions`
    );
  }

  getOrientations(
    institutionName: string,
    startDate?: any,
    endDate?: any
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/institutions/${institutionName}/get_orientations?start_date=${startDate}&end_date=${endDate}`
    );
  }

  getOrientationsByUser(
    advisorId: number,
    studentId: number,
    startDate?: any,
    endDate?: any
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/institutions/${advisorId}/${studentId}/get_orientations?start_date=${startDate}&end_date=${endDate}`
    );
  }

  getOrientationsByProject(
    projectId: number,
    startDate?: any,
    endDate?: any
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/institutions/${projectId}/get_orientations_by_project`
    );
  }

  bindUsers(bind: boolean, users: Array<number>, institution = ""): Observable<any> {
    return this.http.put<any>(
      `${environment.base_url}/institutions/bind_users`,
      { bind, users, institution }
    );
  }

  saveOrientation(
    description: string,
    studentId: number,
    projectId: number,
    startedAt: string,
    endedAt: string
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/institutions/save_orientation`,
      {
        stage_description: description,
        student_id: studentId,
        project_id: projectId,
        started_at: startedAt,
        ended_at: endedAt,
      }
    );
  }

  setOrientationDescription(
    orientationId: number,
    description: string
  ): Observable<any> {
    return this.http.put<any>(
      `${environment.base_url}/institutions/set_orientation_description`,
      { orientation_id: orientationId, orientation_description: description }
    );
  }

  paginateOrientations(
    advisorId: number,
    studentId: number,
    page: number = 1
  ): Observable<Paginate<OrientationDeadline>> {
    return this.http.get<Paginate<OrientationDeadline>>(
      `${environment.base_url}/institutions/${advisorId}/${studentId}/get_paginate_orientations?page=${page}`
    );
  }

  paginateOrientationsByProject(
    projectId: number,
    page: number = 1
  ): Observable<Paginate<OrientationDeadline>> {
    return this.http.get<Paginate<OrientationDeadline>>(
      `${environment.base_url}/institutions/${projectId}/get_paginate_orientations_by_project?page=${page}`
    );
  }

  updateOrientation(
    orientationId: number,
    stageDescription: string,
    startedAt: string,
    endedAt: string
  ): Observable<OrientationDeadline> {
    return this.http.put<OrientationDeadline>(
      `${environment.base_url}/institutions/update_orientation`,
      {
        orientation_id: orientationId,
        stage_description: stageDescription,
        started_at: startedAt,
        ended_at: endedAt,
      }
    );
  }

  getProjectAuthorId(projectId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/institutions/${projectId}/get_project_author_id`
    );
  }

  getProjectAdvisorId(projectId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/institutions/${projectId}/get_project_advisor_id`
    );
  }

  save(form: Institution): Observable<Institution> {
    if (form.id) {
      return this.update(form);
    }
    return this.create(form);
  }

  private create(form: Institution): Observable<Institution> {
    return this.http.post<any>(`${environment.base_url}/institutions`, form);
  }

  private update(form: Institution): Observable<Institution> {
    return this.http.put<any>(`${environment.base_url}/institutions/${form.id}`, form);
  }
}
